// TSP path management
import { useState, useCallback, useRef, useEffect } from 'react';
import { debug } from '../../../../utils/debug';
// Use root-level imports to avoid circular dependencies
import { 
  isAutoMode, 
  isCustomMode, 
  isSetupMode
} from '../../Services';

// Hook for managing TSP paths
export const usePathManagement = (locations) => {
  // Path storage for different path modes
  const [autoPath, setAutoPath] = useState([]);
  const [customPath, setCustomPath] = useState([]);
  
  // Used to prevent circular updates
  const prevAutoPathRef = useRef(null);
  const prevCustomPathRef = useRef(null);
  const isSavingRef = useRef(false);
  
  // Save auto path (now just updates ref for consistency)
  const saveAutoPath = useCallback((path) => {
    prevAutoPathRef.current = path ? [...path] : null;
  }, []);

  // Save custom path (now just updates ref for consistency)
  const saveCustomPath = useCallback((path) => {
    prevCustomPathRef.current = path ? [...path] : null;
  }, []);

  // Set path for current mode
  const setPath = useCallback((path, currentMode, isFullPath = false) => {
    debug('TSP-PATH', 'Setting path', { path, currentMode, isFullPath });
    
    // If path is an array, preserve existing flags and add new ones
    let pathWithFlag = path;
    if (Array.isArray(path) && path.length > 0) {
      // Create a new array to preserve original path data, while adding the flag
      pathWithFlag = [...path];
      // Preserve existing flags from original path
      if (path._isConfirmed !== undefined) {
        pathWithFlag._isConfirmed = path._isConfirmed;
      }
      if (path._isFullPath !== undefined) {
        pathWithFlag._isFullPath = path._isFullPath;
      }
      // Add isFullPath flag directly to the array object
      pathWithFlag._isFullPath = isFullPath;
    }
    
    if (isAutoMode(currentMode)) {
      setAutoPath(pathWithFlag);
      // Update reference for consistency
      if (pathWithFlag && pathWithFlag.length > 1) {
        saveAutoPath(pathWithFlag);
      }
    } else if (isCustomMode(currentMode)) {
      setCustomPath(pathWithFlag);
      // Update reference for consistency
      if (pathWithFlag && pathWithFlag.length > 1) {
        saveCustomPath(pathWithFlag);
      }
    } else if (isSetupMode(currentMode)) {
      // In setup points mode, clear all paths
      setAutoPath([]);
      setCustomPath([]);
    }
  }, [saveAutoPath, saveCustomPath]);

  return {
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath,
    saveAutoPath,
    saveCustomPath
  };
};

// Get active path for current mode
export const getActivePathByMode = (currentMode, autoPath, customPath) => {
  if (isAutoMode(currentMode)) {
    return autoPath;
  } else if (isCustomMode(currentMode)) {
    return customPath;
  }
  return [];
};

 